// frontend/src/components/Settings/Profile.jsx

import React, { useState, useEffect } from 'react';
import { authAPI, usersAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { FiUser, FiLock, FiMail, FiUsers, FiShield, FiCalendar, FiGlobe } from 'react-icons/fi';
import './Settings.css';

const Profile = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Language preference ───────────────────────────────────────────
  const [langCode,    setLangCode]    = useState('pt');
  const [langSaving,  setLangSaving]  = useState(false);
  const [langSuccess, setLangSuccess] = useState('');
  const [langError,   setLangError]   = useState('');

  useEffect(() => {
    if (user?.user_id) {
      usersAPI.getById(user.user_id).then(r => {
        if (r.data.success) setLangCode(r.data.user?.language_code || 'pt');
      }).catch(() => {});
    }
  }, [user?.user_id]);

  useEffect(() => {
    console.log('[Profile.jsx] mounted');
    return () => console.log('[Profile.jsx] unmounted');
  }, []);  

  const handleLangSave = async () => {
    setLangSaving(true); setLangSuccess(''); setLangError('');
    try {
      await usersAPI.update(user.user_id, { languageCode: langCode });
      setLangSuccess(t('profile.language_saved', 'Idioma guardado. Recarregue a página para aplicar.'));
    } catch (err) {
      setLangError(err.response?.data?.error || 'Erro ao guardar idioma');
    } finally { setLangSaving(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError(t('profile.error_all_fields_required', 'All fields are required'));
      return;
    }
    if (formData.newPassword.length < 6) {
      setError(t('profile.error_password_min_length', 'New password must be at least 6 characters long'));
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('profile.error_passwords_dont_match', 'New password and confirmation do not match'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      if (response.data.success) {
        setSuccess(t('profile.password_changed_success', 'Password changed successfully'));
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('profile.error_change_failed', 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeBadgeClass = (userType) => {
    switch (userType) {
      case 'admin':   return 'badge-admin';
      case 'manager': return 'badge-manager';
      default:        return 'badge-user';
    }
  };

  return (
    <div className="settings-page">

      {/* Page Header */}
      <div className="form-header">
        <div>
          <h2>{t('profile.title', 'My Profile')}</h2>
          <p className="order-number">{t('profile.subtitle', 'Manage your account settings and security')}</p>
        </div>
      </div>

      <div className="profile-layout">

        {/* User Information */}
        <div className="form-section">
          <h3>{t('profile.user_information', 'User Information')}</h3>

          <div className="profile-info-grid">

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiUser size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.username', 'Username')}</div>
                <div className="profile-info-value">{user?.username || '—'}</div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiUser size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.full_name', 'Full Name')}</div>
                <div className="profile-info-value">{user?.full_name || '—'}</div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiMail size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.email', 'Email')}</div>
                <div className="profile-info-value">{user?.email || t('profile.not_set', 'Not set')}</div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiUsers size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.group', 'Group')}</div>
                <div className="profile-info-value">{user?.group_name || t('profile.no_group', 'Grupos geridos pelo administrador')}</div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiShield size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.user_type', 'User Type')}</div>
                <div className="profile-info-value">
                  <span className={`profile-badge ${getUserTypeBadgeClass(user?.userType)}`}>
                    {user?.userType || 'user'}
                  </span>
                </div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiGlobe size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.language', 'Language')}</div>
                <div className="profile-info-value">{user?.languageCode?.toUpperCase() || '—'}</div>
              </div>
            </div>

            <div className="profile-info-item">
              <span className="profile-info-icon"><FiCalendar size={16} /></span>
              <div>
                <div className="profile-info-label">{t('profile.last_login', 'Last Login')}</div>
                <div className="profile-info-value">
                  {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Change Password */}
        <div className="form-section">
          <h3>{t('profile.change_password', 'Change Password')}</h3>

          {error && (
            <div className="profile-alert profile-alert-error">
              <span>⚠️</span>
              {error}
            </div>
          )}
          {success && (
            <div className="profile-alert profile-alert-success">
              <span>✓</span>
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid form-grid-2" style={{ maxWidth: 640 }}>

              <div className="form-group full-width">
                <label>{t('profile.current_password', 'Current Password')}</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('profile.new_password', 'New Password')}</label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder={t('profile.min_8_chars', 'Min. 6 characters')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('profile.confirm_password', 'Confirm New Password')}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

            </div>

            <div style={{ marginTop: 16 }}>
              <button type="submit" className="btn-primary" disabled={loading}>
                <FiLock size={16} />
                {loading
                  ? t('profile.changing_password', 'Changing Password...')
                  : t('profile.change_password_btn', 'Change Password')}
              </button>
            </div>
          </form>
        </div>

        {/* Language Preference */}
        <div className="form-section">
          <h3>{t('profile.language_pref', 'Preferência de Idioma')}</h3>

          {langError && (
            <div className="profile-alert profile-alert-error">
              <span>⚠️</span> {langError}
            </div>
          )}
          {langSuccess && (
            <div className="profile-alert profile-alert-success">
              <span>✓</span> {langSuccess}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, maxWidth: 320 }}>
            <div className="form-group" style={{ flex: 1, margin: 0 }}>
              <label>{t('profile.language', 'Idioma')}</label>
              <select
                value={langCode}
                onChange={e => { setLangCode(e.target.value); setLangSuccess(''); setLangError(''); }}
                className="form-control"
              >
                <option value="pt">🇵🇹 Português</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={handleLangSave}
              disabled={langSaving}
              style={{ flexShrink: 0 }}
            >
              <FiGlobe size={15}/>
              {langSaving ? t('saving', 'A guardar…') : t('save', 'Guardar')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;